import { defineStore } from 'pinia'
import auth from '@/api/auth'
import bus from '@/global/bus'
import { clearPreferredCaseId } from '@/global/preferredCase'
import { useUserStore } from '@/stores/user'
import { t } from '@/global/i18n'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: localStorage.getItem('auth_token') || null,
    isLoggedIn: !!localStorage.getItem('auth_token') || false,
    bankIdOrderReference: localStorage.getItem('bankid_order_reference') || null,
    mitIdOrderReference: localStorage.getItem('mitid_order_reference') || null,
    hasAuthLogoutListener: false,
  }),
  actions: {
    clearAuthState() {
      this.token = null
      this.isLoggedIn = false
      this.bankIdOrderReference = null
      this.mitIdOrderReference = null

      // Clear all auth-related data from localStorage
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('bankid_order_reference')
      localStorage.removeItem('mitid_order_reference')
      // A deep-link target must not survive a logout or be replayed for the
      // next user on a shared browser.
      clearPreferredCaseId()
    },

    registerAuthListeners() {
      if (this.hasAuthLogoutListener) return
      this.hasAuthLogoutListener = true

      bus.on('auth-logout', () => {
        this.clearAuthState()
      })
    },

    // Sync store state with localStorage (useful for cross-tab/window scenarios)
    syncFromLocalStorage() {
      this.token = localStorage.getItem('auth_token') || null
      this.isLoggedIn = !!localStorage.getItem('auth_token')
      this.bankIdOrderReference = localStorage.getItem('bankid_order_reference') || null
      this.mitIdOrderReference = localStorage.getItem('mitid_order_reference') || null
    },

    async login(credentials) {
      try {
        await auth.login(credentials)
        this.token = localStorage.getItem('auth_token')
        this.isLoggedIn = true
      } catch (error) {
        console.error(t('errors.api.auth.loginFailed'), error)
        throw error
      }
    },

    logout() {
      this.clearAuthState()

      bus.emit('auth-logout')

      // Clear user profile on logout
      const userStore = useUserStore()
      userStore.clearUserProfile()
    },

    async requestMagicLink(email, companySlug, leasing = false) {
      try {
        if (leasing) {
          return await auth.requestLeasingMagicLink(email)
        }
        return await auth.requestMagicLink(email, companySlug)
      } catch (error) {
        console.error(t('errors.api.auth.magicLinkRequestFailed'), error)
        throw error
      }
    },

    async verifyMagicLink(token, challenge, leasing = false) {
      try {
        if (leasing) {
          await auth.verifyLeasingMagicLink(token, challenge)
        } else {
          await auth.verifyMagicLink(token, challenge)
        }
        this.token = localStorage.getItem('auth_token')
        this.isLoggedIn = true
      } catch (error) {
        console.error(t('errors.api.auth.magicLinkVerifyFailed'), error)
        throw error
      }
    },

    async requestBankId(companySlug) {
      try {
        const response = await auth.requestBankId(companySlug)

        // Store orderReference in localStorage if present
        if (response.orderReference) {
          this.bankIdOrderReference = response.orderReference
          localStorage.setItem('bankid_order_reference', response.orderReference)
        }

        return response
      } catch (error) {
        console.error(t('errors.api.auth.bankIdRequestFailed'), error)
        throw error
      }
    },

    async verifyBankId(orderReference, companySlug) {
      try {
        const response = await auth.verifyBankId(orderReference, companySlug)

        // Only store tokens and login if status is complete and tokens are present
        if (response.status === 'complete' && response.accessToken && response.refreshToken) {
          auth.setTokens(response.accessToken, response.refreshToken)
          this.token = response.accessToken
          this.isLoggedIn = true
          // Clear order reference after successful verification
          this.clearBankIdOrderReference()
        }

        return response
      } catch (error) {
        console.error(t('errors.api.auth.bankIdVerifyFailed'), error)
        throw error
      }
    },

    async verifyImpersonation(guid, secret) {
      try {
        const response = await auth.verifyImpersonation(guid, secret)

        // Only impersonate if tokens are present and we have either a company slug or leasing flag
        if (
          response.accessToken &&
          response.refreshToken &&
          ((response.companySlug && response.companySlug.length > 0) || response.isLeasing)
        ) {
          this.token = response.accessToken
          this.isLoggedIn = true

          const company = response.isLeasing ? 'leasing' : response.companySlug

          return { company }
        }
      } catch (error) {
        console.error(t('errors.api.auth.impersonationVerifyFailed'), error)
        throw error
      }
    },

    clearBankIdOrderReference() {
      this.bankIdOrderReference = null
      localStorage.removeItem('bankid_order_reference')
    },

    async requestMitId(companySlug) {
      try {
        const response = await auth.requestMitId(companySlug)

        // Store orderReference in localStorage if present
        if (response.orderReference) {
          this.mitIdOrderReference = response.orderReference
          localStorage.setItem('mitid_order_reference', response.orderReference)
        }

        return response
      } catch (error) {
        console.error(t('errors.api.auth.mitIdRequestFailed'), error)
        throw error
      }
    },

    clearMitIdOrderReference() {
      this.mitIdOrderReference = null
      localStorage.removeItem('mitid_order_reference')
    },

    async verifyMitId(orderReference, companySlug) {
      try {
        const response = await auth.verifyMitId(orderReference, companySlug)

        // Only store tokens and login if status is complete and tokens are present
        if (response.status === 'complete' && response.accessToken && response.refreshToken) {
          auth.setTokens(response.accessToken, response.refreshToken)
          this.token = response.accessToken
          this.isLoggedIn = true
          // Clear order reference after successful verification
          this.clearMitIdOrderReference()
        }

        return response
      } catch (error) {
        console.error(t('errors.api.auth.mitIdVerifyFailed'), error)
        throw error
      }
    },
  },
})
